import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Building, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { useTranslation } from '../contexts/LanguageContext';

// Strip HTML tags for plain text preview
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function PropertyCard({ property, viewMode = 'grid' }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const statusLabels = {
    active: t('property.in_construction'),
    completed: t('property.completed'),
    upcoming: t('properties.status_upcoming')
  };

  const statusColors = {
    active: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    upcoming: 'bg-blue-100 text-blue-800'
  };

  // Image 1 (index 0): logo = main_image
  // Image 2 (index 1): cover = shown on hover and as hero in property page
  const logoImage = property.main_image || property.images?.[0];
  const coverImage = property.images?.[1] || property.images?.[0] || logoImage;
  const hasSecondImage = coverImage && coverImage !== logoImage;
  const mainImage = coverImage || logoImage;

  if (viewMode === 'list') {
    return (
      <Link to={`/properties/${property.id}`}
        className="flex gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-md transition-all group">
        <div className="relative w-32 h-28 flex-none rounded-lg overflow-hidden bg-secondary">
          {mainImage ? (
            <img src={mainImage} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Building className="w-8 h-8 opacity-30" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors truncate">{property.name}</h3>
            <Badge className={statusColors[property.status]}>{statusLabels[property.status]}</Badge>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
            <MapPin className="w-3 h-3 flex-none" />{property.location}
          </p>
          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
            {stripHtml(property.description).substring(0, 120)}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/properties/${property.id}`}
      className="property-card flex flex-col h-full bg-white border border-border rounded-xl overflow-hidden group shadow-sm hover:shadow-lg transition-shadow duration-300"
      data-testid={`property-card-${property.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative h-64 flex-none overflow-hidden bg-muted">
        {/* Logo image */}
        <img
          src={logoImage || '/og-image.jpg'}
          alt={property.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
            hovered && hasSecondImage ? 'opacity-0 scale-110' : 'opacity-100 scale-100'
          }`}
        />
        {/* Cover image - shown on hover */}
        {hasSecondImage && (
          <img
            src={coverImage}
            alt={property.name}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
              hovered ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
            }`}
          />
        )}
        <div className="absolute top-4 left-4 z-10">
          <Badge className={statusColors[property.status]}>
            {statusLabels[property.status]}
          </Badge>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-heading font-semibold text-xl text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {property.name}
        </h3>
        <div className="flex items-center gap-2 text-muted-foreground mb-3">
          <MapPin className="w-4 h-4 flex-none" />
          <span className="text-sm truncate">{property.location}</span>
        </div>
        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
          {stripHtml(property.description)}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building className="w-4 h-4" />
            <span>{property.category === 'residential' ? t('property.residential') : property.category}</span>
          </div>
          <span className="flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
            {t('home.view_details')}
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
